"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Lock,
  Unlock,
  Save,
  Loader2,
  Mountain,
  Clapperboard,
} from "lucide-react";
import jsPDF from "jspdf";
import { generateBlockPosition } from "../service/fractional-indexing";

interface Block {
  id: string;
  type: "SCENE_HEADING" | "ACTION";
  text: string;
  position?: string;
}

interface ScriptBriefEditorViewProps {
  type: "LOGLINE" | "SYNOPSIS" | "STORY";
  initialBlocks?: Block[];
  isLocked?: boolean;
  onToggleLock?: () => void;
  onAutosave?: (blocks: Block[]) => void;
}

const LIMITS = {
  LOGLINE: 100,
  SYNOPSIS: 1000,
  STORY: Infinity,
};

// Separate component for block item to handle cursor jumping in contentEditable
const BriefBlockItem = ({
  block,
  isLocked,
  focusedBlockId,
  setFocusedBlockId,
  handleBlockTextChange,
  handleEnterOnBlock,
  handleDeleteBlock,
  handleMoveFocus,
  isOverLimit,
  currentLimit,
  currentCount,
}: {
  block: Block;
  isLocked: boolean;
  focusedBlockId: string;
  setFocusedBlockId: (id: string) => void;
  handleBlockTextChange: (id: string, text: string) => void;
  handleEnterOnBlock: (id: string) => void;
  handleDeleteBlock: (id: string) => void;
  handleMoveFocus: (id: string, direction: "up" | "down") => void;
  isOverLimit: boolean;
  currentLimit: number;
  currentCount: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Sync text with DOM without triggering re-renders that reset cursor
  useEffect(() => {
    if (ref.current && ref.current.innerText !== block.text) {
      ref.current.innerText = block.text;
    }
  }, [block.text]);

  // Focus effect
  useEffect(() => {
    if (ref.current && focusedBlockId === block.id) {
      // Use setTimeout to ensure the DOM is ready before focusing
      setTimeout(() => {
        if (!ref.current) return;
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
        ref.current.focus();
      }, 50);
    }
  }, [focusedBlockId, block.id]);

  return (
    <div
      className={`relative mb-4 outline-none ${
        focusedBlockId === block.id ? "bg-zinc-50/50" : ""
      }`}
      onClick={() => setFocusedBlockId(block.id)}
    >
      {block.type === "SCENE_HEADING" ? (
        <div
          ref={ref}
          contentEditable={!isLocked}
          data-placeholder="ENTER HEADING"
          className={`uppercase font-bold text-zinc-900 flex items-center gap-3 relative ${
            isLocked ? "cursor-not-allowed" : ""
          } ${block.text === "" ? "before:content-[attr(data-placeholder)] before:text-zinc-400 before:normal-case before:font-normal before:text-sm" : ""}`}
          style={{
            maxWidth: "6.0in",
            outline: "none",
            whiteSpace: "pre-wrap",
          }}
          onInput={(e) =>
            handleBlockTextChange(block.id, e.currentTarget.innerText)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleEnterOnBlock(block.id);
            }
          }}
          suppressContentEditableWarning={true}
        />
      ) : (
        <div
          ref={ref}
          contentEditable={!isLocked}
          data-placeholder="Write your brief here..."
          className={`text-zinc-800 outline-none whitespace-pre-wrap relative ${
            isLocked ? "cursor-not-allowed" : ""
          } ${isOverLimit ? "bg-rose-50/50" : ""} ${block.text === "" ? "before:content-[attr(data-placeholder)] before:text-zinc-400 before:text-sm" : ""}`}
          style={{
            maxWidth: "6.0in",
            lineHeight: "15pt",
            fontSize: "1rem",
          }}
          onInput={(e) =>
            handleBlockTextChange(block.id, e.currentTarget.innerText)
          }
          suppressContentEditableWarning={true}
          onKeyDown={(e) => {
            if (isLocked) return;

            // Enter goes to next block or creates a new one
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleEnterOnBlock(block.id);
              return;
            }

            // Backspace in empty block deletes it
            if (e.key === "Backspace" && block.text === "") {
              e.preventDefault();
              handleDeleteBlock(block.id);
              return;
            }

            // Arrow keys to navigate blocks
            if (e.key === "ArrowUp") {
              e.preventDefault();
              handleMoveFocus(block.id, "up");
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              handleMoveFocus(block.id, "down");
              return;
            }

            // Enforce limit but allow completing the current word
            if (currentCount >= currentLimit) {
              // Always prevent space to avoid starting a new word
              if (e.key === " ") {
                e.preventDefault();
                return;
              }

              // If strictly over limit, or at limit and trying to start a new word
              const text = block.text;
              const endsWithSpace = /\s$/.test(text);
              const isAllowedKey =
                e.key === "Backspace" ||
                e.key === "Delete" ||
                e.key === "Enter" ||
                e.ctrlKey ||
                e.metaKey;

              if (!isAllowedKey) {
                if (
                  currentCount > currentLimit ||
                  (currentCount === currentLimit && endsWithSpace)
                ) {
                  e.preventDefault();
                }
              }
            }
          }}
        />
      )}
    </div>
  );
};

export function ScriptBriefEditorView({
  type,
  initialBlocks,
  isLocked = false,
  onToggleLock,
  onAutosave,
}: ScriptBriefEditorViewProps) {
  const [blocks, setBlocks] = useState<Block[]>(() => {
    if (initialBlocks && initialBlocks.length >= 2) {
      return initialBlocks;
    }
    return [
      {
        id: "initscene",
        position: "m_initscene",
        type: "SCENE_HEADING",
        text: "",
      },
    ];
  });
  const [focusedBlockId, setFocusedBlockId] = useState<string>("initscene");
  const [autosaveStatus, setAutosaveStatus] = useState<
    "saved" | "saving" | "typing" | "error" | "idle"
  >("idle");

  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";

  useEffect(() => {
    const fontId = "google-font-courier-prime";
    const styleId = "script-brief-editor-force";

    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        [data-script-brief-editor] *, 
        [data-script-brief-editor] div, 
        [data-script-brief-editor] span, 
        [data-script-brief-editor] [contenteditable="true"] {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const isFirstRender = useRef(true);
  const onAutosaveRef = useRef(onAutosave);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    onAutosaveRef.current = onAutosave;
  }, [onAutosave]);

  // Auto-save logic
  useEffect(() => {
    if (isLocked) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setAutosaveStatus("typing");
    
    // Clear any pending timers from previous renders
    if (timerRef.current) clearTimeout(timerRef.current);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    timerRef.current = setTimeout(() => {
      setAutosaveStatus("saving");
      onAutosaveRef.current?.(blocks);
      
      saveTimerRef.current = setTimeout(() => {
        setAutosaveStatus("saved");
        
        idleTimerRef.current = setTimeout(() => {
          setAutosaveStatus((prev) => (prev === "saved" ? "idle" : prev));
        }, 2000);
      }, 500);
    }, 2000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [blocks, isLocked]);

  const handleBlockTextChange = (id: string, text: string) => {
    setBlocks((prev) =>
      prev.map((block) => (block.id === id ? { ...block, text } : block)),
    );
  };

  const getWordCount = (text: string) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const downloadPdf = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "letter",
    });

    doc.setFont("courier", "bold");
    doc.setFontSize(12);

    const headingBlock = blocks.find((b) => b.type === "SCENE_HEADING");
    const heading = headingBlock ? headingBlock.text.toUpperCase() : type;

    const actionBlock = blocks.find((b) => b.type === "ACTION");
    const content = actionBlock ? actionBlock.text : "";

    const MARGIN_LEFT = 38.1; // 1.5 inches
    const MARGIN_TOP = 25.4; // 1 inch
    const WIDTH = 152.4; // 6 inches

    let y = MARGIN_TOP;

    doc.text(heading, MARGIN_LEFT, y);
    y += 12;

    doc.setFont("courier", "normal");
    const lines = doc.splitTextToSize(content, WIDTH);

    lines.forEach((line: string) => {
      if (y > doc.internal.pageSize.getHeight() - 25.4) {
        doc.addPage();
        y = 25.4;
      }
      doc.text(line, MARGIN_LEFT, y);
      y += 6;
    });

    doc.save(`${type.toLowerCase()}_export.pdf`);
  };

  const handleEnterOnBlock = (id: string) => {
    let newId = "";
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === id);
      if (index === -1) return prev;

      // If there is a next block of type ACTION, move to it instead of creating
      if (index < prev.length - 1 && prev[index + 1].type === "ACTION") {
        newId = prev[index + 1].id;
        return prev;
      }

      const currentBlock = prev[index];
      const nextBlock = prev[index + 1] || null;

      newId = Math.random().toString(36).substr(2, 9);
      const position = generateBlockPosition(
        currentBlock.position || null,
        nextBlock?.position || null,
        newId,
      );

      const newBlock: Block = {
        id: newId,
        type: "ACTION",
        text: "",
        position: position,
      };
      const newBlocks = [...prev];
      newBlocks.splice(index + 1, 0, newBlock);
      return newBlocks;
    });

    if (newId) {
      setFocusedBlockId(newId);
    }
  };

  const handleDeleteBlock = (id: string) => {
    setBlocks((prev) => {
      const index = prev.findIndex((b) => b.id === id);
      if (index === -1) return prev;

      const blockToDelete = prev[index];
      if (blockToDelete.type === "SCENE_HEADING") return prev; // Can't delete heading

      // Keep at least one action block
      const actionBlocks = prev.filter((b) => b.type === "ACTION");
      if (actionBlocks.length <= 1) return prev;

      const newBlocks = prev.filter((b) => b.id !== id);

      // Focus previous block
      const prevBlock = prev[index - 1];
      if (prevBlock) {
        setFocusedBlockId(prevBlock.id);
      }

      return newBlocks;
    });
  };

  const handleMoveFocus = (id: string, direction: "up" | "down") => {
    const index = blocks.findIndex((b) => b.id === id);
    if (index === -1) return;

    if (direction === "up" && index > 0) {
      setFocusedBlockId(blocks[index - 1].id);
    } else if (direction === "down" && index < blocks.length - 1) {
      setFocusedBlockId(blocks[index + 1].id);
    }
  };

  const currentLimit = LIMITS[type] || Infinity;
  const actionBlocks = blocks.filter((b) => b.type === "ACTION");
  const currentCount = actionBlocks.reduce(
    (acc, b) => acc + getWordCount(b.text),
    0,
  );
  const isOverLimit = currentCount > currentLimit;

  return (
    <div className="w-full min-h-screen bg-zinc-50 flex flex-col items-center">
      {/* Sticky Toolbar - Exactly like screenplay-editor.view.tsx */}
      <div className="sticky top-0 left-0 right-0 z-50 w-full bg-zinc-50/95 backdrop-blur-md border-b border-zinc-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.01)] flex flex-wrap lg:flex-nowrap items-center justify-between px-3 sm:px-6 py-2 mb-8 select-none transition-all gap-y-2">
        {/* Left Section: Branding & Status */}
        <div className="flex items-center gap-3 w-auto shrink-0 order-1">
          <span className="text-[11px] font-bold tracking-wider text-blumine-400/80 uppercase">
            {type}
          </span>
          {autosaveStatus === "saved" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100/50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Synced
              </span>
            </div>
          )}
          {autosaveStatus === "saving" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100/50">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              <span className="text-[10px] font-semibold tracking-wide">
                Saving...
              </span>
            </div>
          )}
          {autosaveStatus === "typing" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100/50">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Drafting...
              </span>
            </div>
          )}
          {isOverLimit && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100/50">
              <span className="text-[10px] font-semibold tracking-wide">
                Over Limit
              </span>
            </div>
          )}
        </div>

        {/* Right Section: Utilities */}
        <div className="flex items-center justify-end gap-1 sm:gap-1.5 w-auto lg:w-[220px] shrink-0 order-2 lg:order-3">
          {/* Word Count Badge */}
          <div className="px-3 py-1 text-xs font-mono text-zinc-500 bg-white border border-zinc-200 rounded-lg">
            {currentCount}/{currentLimit === Infinity ? "∞" : currentLimit}
          </div>

          {/* Lock Button */}
          {onToggleLock && (
            <button
              onClick={onToggleLock}
              className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition active:scale-95 ${
                isLocked
                  ? "text-rose-500 hover:bg-rose-50 hover:text-rose-700 bg-rose-50/50"
                  : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"
              }`}
              title={isLocked ? "Unlock" : "Lock"}
            >
              {isLocked ? (
                <Lock className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <Unlock className="w-4 h-4" strokeWidth={1.5} />
              )}
            </button>
          )}

          {/* Save Button (Downloads PDF) */}
          <button
            onClick={downloadPdf}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition active:scale-95"
            title="Download PDF"
          >
            <Save className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Editor Page */}
      <div className="flex flex-col gap-12 pb-24 w-full items-center justify-center px-4">
        <div
          className="relative bg-[#fdfdfc] shadow-2xl shadow-zinc-300/60 border border-zinc-100 rounded-sm md:rounded-md flex flex-col w-[210mm] min-h-[297mm] shrink-0 p-[1in]"
          data-script-brief-editor="true"
          style={{ lineHeight: "1.2" }}
        >
          {blocks.map((block) => (
            <BriefBlockItem
              key={block.id}
              block={block}
              isLocked={isLocked}
              focusedBlockId={focusedBlockId}
              setFocusedBlockId={setFocusedBlockId}
              handleBlockTextChange={handleBlockTextChange}
              handleEnterOnBlock={handleEnterOnBlock}
              handleDeleteBlock={handleDeleteBlock}
              handleMoveFocus={handleMoveFocus}
              isOverLimit={isOverLimit}
              currentLimit={currentLimit}
              currentCount={currentCount}
            />
          ))}

          {/* Guidelines / Helper */}
          <div className="absolute bottom-8 left-[1in] right-[1in] text-[11px] text-zinc-400 font-medium select-none">
            Spacing and typography follow standard Screenplay Writers
            Association (SWA) guidelines.
          </div>
        </div>
      </div>
    </div>
  );
}
